/**
 * Display-only pseudo-currencies that money surfaces MUST ignore. The
 * CurrencyAvailability proto contract (see the "TOURNAMENT_PTS currency
 * convention" note on the generated message in
 * generated/bay_session_shared_pb.ts) mandates that producers/consumers
 * routing real money ignore TOURNAMENT_PTS the same way they ignore the
 * PRACTICE display currency: it carries a tournament score (1:1 units, no
 * cents scaling), not a wallet balance, so rendering it as $ would show the
 * score as cents.
 */
const DISPLAY_ONLY_CURRENCIES = new Set(['TOURNAMENT_PTS', 'PRACTICE']);

/**
 * SMG-3250: total displayed wallet balance (minor units / cents) from a
 * PlayerState's per-currency funds projection (PlayerState.funds, field 100,
 * populated server-side per SMG-3198). Matches the kiosk's balanceFromFunds —
 * the USD row's available + locked (= withdrawable + bonus, won included),
 * falling back to the first WALLET-BACKED row when USD is absent.
 *
 * Display-only rows (DISPLAY_ONLY_CURRENCIES, e.g. a tournament
 * participant's TOURNAMENT_PTS score) are excluded BEFORE the fallback, and
 * a list holding only such rows behaves exactly like an empty one. Returns
 * null when the frame carries no money funds: a degraded / minimal frame —
 * or a pts-only tournament frame whose wallet enrichment didn't run — must
 * NOT clobber a good displayed balance.
 */
export function balanceFromPlayerFunds(
  funds: ReadonlyArray<{currency: string; available: bigint; locked: bigint}> | undefined,
): number | null {
  const moneyFunds = (funds ?? []).filter((f) => !DISPLAY_ONLY_CURRENCIES.has(f.currency));
  if (moneyFunds.length === 0) return null;
  const chosen = moneyFunds.find((f) => f.currency === 'USD') ?? moneyFunds[0];
  if (!chosen) return null;
  return Number(chosen.available ?? 0n) + Number(chosen.locked ?? 0n);
}
